import React, { useEffect, useState, useCallback, useRef, useLayoutEffect } from 'react';
import {
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonSpinner,
  IonRefresher,
  IonRefresherContent,
  IonText,
  IonIcon,
  IonToast,
  IonPopover,
  IonModal,
  IonAlert,
  RefresherEventDetail,
} from '@ionic/react';
import { albumsOutline, chevronDownOutline, chevronForwardOutline, cloudOfflineOutline, lockClosedOutline, lockOpenOutline, optionsOutline, pricetagOutline, trendingDownOutline } from 'ionicons/icons';
import {
  getAllListings,
  getMinimums,
  setMinimum,
  setMinimumsBulk,
  getLockedMinimums,
  setMinimumsLocked,
  setHelloListingsStatus,
  ListingsData,
  NormalisedEvent,
  CategoryRow,
  TargetListing,
  ListingMinimumItem,
} from '../services/listingsService';
import { standardizeEventName } from '../utils/standardize';
import { format } from 'date-fns';

function fmt(price: number | null | undefined, currency: string = '£'): string {
  if (price == null) return '–';
  return `${currency}${price.toFixed(2)}`;
}

/**
 * Always-present number input for the "Min" column so native Tab moves between
 * rows. Commits on blur (incl. tabbing away) and Enter, only when the value
 * actually changed.
 */
const MinInput: React.FC<{
  value: number | null;
  onCommit: (raw: string) => Promise<boolean>;
  placeholder?: string;
  title?: string;
}> = ({ value, onCommit, placeholder = '—', title }) => {
  const [text, setText] = useState(value == null ? '' : String(value));
  const [saved, setSaved] = useState(false);
  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Re-sync when the underlying value changes (e.g. after a reload)
  useEffect(() => {
    setText(value == null ? '' : String(value));
  }, [value]);

  // Clear any pending timer on unmount
  useEffect(() => () => {
    if (savedTimer.current) clearTimeout(savedTimer.current);
  }, []);

  const flashSaved = () => {
    setSaved(true);
    if (savedTimer.current) clearTimeout(savedTimer.current);
    savedTimer.current = setTimeout(() => setSaved(false), 1000);
  };

  const commit = async () => {
    const newNum = text.trim() === '' ? null : Number(text);
    if (newNum != null && isNaN(newNum)) return; // ignore garbage
    if (newNum === value) return; // unchanged — don't fire a save
    const ok = await onCommit(text);
    if (ok) flashSaved();
  };

  return (
    <input
      type="number"
      inputMode="decimal"
      step="0.01"
      min="0"
      value={text}
      placeholder={placeholder}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          (e.target as HTMLInputElement).blur();
        } else if (e.key === 'Escape') {
          setText(value == null ? '' : String(value));
          (e.target as HTMLInputElement).blur();
        }
      }}
      onClick={(e) => e.stopPropagation()}
      title={title}
      className={`w-20 text-right text-sm rounded px-2 py-1 text-gray-900 dark:text-gray-100 focus:outline-none transition-colors duration-300 ${
        saved
          ? 'border border-green-500 ring-1 ring-green-500 bg-green-50 dark:bg-green-900/30'
          : 'bg-transparent border border-gray-300 dark:border-gray-600 focus:border-primary-500 focus:ring-1 focus:ring-primary-500'
      }`}
    />
  );
};

type CellColor = 'green' | 'blue' | 'red' | 'amber' | null;

/**
 * green  — my price is cheapest (or no competitor)
 * blue   — minimum set but minimum > comp price (bot is blocked from undercutting)
 * red    — no minimum set on ANY of the split's listings — genuinely unprotected
 * amber  — SOME of the split's listings have a minimum and some don't line up cleanly: either every
 *          listing has one but they disagree (e.g. a legacy per-listing floor next to one just set
 *          via the dashboard), or only some of them have one at all (e.g. new/relisted tickets
 *          scraped in after a bulk edit that never got covered). Either way at least one listing IS
 *          protected — this just flags that the dashboard can't show one clean number. NOT the same
 *          risk as red (genuinely zero protection).
 * null   — has a uniform minimum, can update, not cheapest yet (neutral)
 */
function getPlatformColor(
  hasListings: boolean,
  myPrice: number | null,
  compPrice: number | null,
  minimum: number | null,
  mixed = false,
): CellColor {
  if (!hasListings) return null;
  if (mixed) return 'amber'; // set on every listing, but not to the same value — not "missing"
  if (minimum == null) return 'red';  // no minimum set — always red, even if cheapest
  if (myPrice != null && (compPrice == null || myPrice <= compPrice)) return 'green';
  if (compPrice != null && minimum >= compPrice) return 'blue';
  return null;
}

const COLOR_CLASS: Record<NonNullable<CellColor>, string> = {
  green: 'bg-green-50 dark:bg-green-900/20',
  blue:  'bg-blue-50  dark:bg-blue-900/20',
  red:   'bg-red-50   dark:bg-red-900/20',
  amber: 'bg-amber-50 dark:bg-amber-900/20',
};

interface MergedSplit {
  splitType: string;
  liveMine: number | null;
  liveComp: number | null;
  ftnMine: number | null;
  ftnComp: number | null;
  helloMine: number | null;
  helloComp: number | null;
  helloMineUsd: number | null;
  helloCompUsd: number | null;
  stubhubMine: number | null;
  stubhubComp: number | null;
  vividMine: number | null;
  vividComp: number | null;
  liveListingIds: string[];
  ftnListingIds: string[];
  helloListingIds: string[];
  stubhubListingIds: string[];
  vividListingIds: string[];
  minimum: number | null;
  liveMinimum: number | null;
  ftnMinimum: number | null;
  helloMinimum: number | null;
  stubhubMinimum: number | null;
  vividMinimum: number | null;
  // True when this platform's underlying listing IDs for this split are NOT cleanly "all one uniform
  // value" — either some carry a legacy per-listing value from before a bulk edit while others carry
  // the value just set, OR some of the ids simply have no minimumPrice doc at all yet (e.g. a ticket
  // that turned over / relisted with a new listingId after the last bulk edit ran). As long as AT
  // LEAST ONE id has a stored minimum, this is true rather than fully missing — without it, the
  // cell's `xMinimum` collapses to null exactly like "never set", which reads as every floor having
  // vanished when really only the newest listing(s) need re-covering.
  liveMinMixed: boolean;
  ftnMinMixed: boolean;
  helloMinMixed: boolean;
  stubhubMinMixed: boolean;
  vividMinMixed: boolean;
  // How many of this platform's listing ids in the split carry a LOCKED minimum. A locked minimum is
  // frozen: every dashboard write skips those ids (the server enforces it too) and the bots leave the
  // listing's price alone. Equal to the id count ⇒ the whole cell is read-only.
  liveLocked: number;
  ftnLocked: number;
  helloLocked: number;
  stubhubLocked: number;
  vividLocked: number;
}

// Platform registry — drives the per-platform minimum popover and split detection.
type PlatformKey = 'live' | 'ftn' | 'hello' | 'stubhub' | 'vivid';
const PLATFORMS: {
  key: PlatformKey;
  label: string;
  dot: string;
  ids: 'liveListingIds' | 'ftnListingIds' | 'helloListingIds' | 'stubhubListingIds' | 'vividListingIds';
  min: 'liveMinimum' | 'ftnMinimum' | 'helloMinimum' | 'stubhubMinimum' | 'vividMinimum';
  minMixed: 'liveMinMixed' | 'ftnMinMixed' | 'helloMinMixed' | 'stubhubMinMixed' | 'vividMinMixed';
  mine: 'liveMine' | 'ftnMine' | 'helloMine' | 'stubhubMine' | 'vividMine';
  comp: 'liveComp' | 'ftnComp' | 'helloComp' | 'stubhubComp' | 'vividComp';
  locked: 'liveLocked' | 'ftnLocked' | 'helloLocked' | 'stubhubLocked' | 'vividLocked';
}[] = [
  { key: 'live',    label: 'Live',    dot: 'bg-indigo-500', ids: 'liveListingIds',    min: 'liveMinimum',    minMixed: 'liveMinMixed',    mine: 'liveMine',    comp: 'liveComp', locked: 'liveLocked' },
  { key: 'ftn',     label: 'FTN',     dot: 'bg-amber-500',  ids: 'ftnListingIds',     min: 'ftnMinimum',     minMixed: 'ftnMinMixed',     mine: 'ftnMine',     comp: 'ftnComp', locked: 'ftnLocked' },
  { key: 'hello',   label: 'Hello',   dot: 'bg-teal-500',   ids: 'helloListingIds',   min: 'helloMinimum',   minMixed: 'helloMinMixed',   mine: 'helloMine',   comp: 'helloComp', locked: 'helloLocked' },
  { key: 'stubhub', label: 'StubHub', dot: 'bg-violet-500', ids: 'stubhubListingIds', min: 'stubhubMinimum', minMixed: 'stubhubMinMixed', mine: 'stubhubMine', comp: 'stubhubComp', locked: 'stubhubLocked' },
  { key: 'vivid',   label: 'Vivid',   dot: 'bg-rose-500',   ids: 'vividListingIds',   min: 'vividMinimum',   minMixed: 'vividMinMixed',   mine: 'vividMine',   comp: 'vividComp', locked: 'vividLocked' },
];

// Aggregate title color for an event from every present platform cell across all splits:
// red if any minimum is missing, amber if any is set-but-mixed, else blue if any cell is blue,
// else green if all are cheapest (green), else neutral.
const TITLE_COLOR_CLASS: Record<NonNullable<CellColor>, string> = {
  green: 'text-green-600 dark:text-green-400',
  blue:  'text-blue-600  dark:text-blue-400',
  red:   'text-red-600   dark:text-red-400',
  amber: 'text-amber-600 dark:text-amber-400',
};

type PlatformMeta = (typeof PLATFORMS)[number];

// Aggregate title color over the given platform pool (default: all).
function getEventTitleColor(event: MergedEvent, pool: PlatformMeta[] = PLATFORMS): CellColor {
  let anyRed = false;
  let anyAmber = false;
  let anyBlue = false;
  let anyCell = false;
  let allGreen = true;
  for (const cat of event.categories) {
    for (const s of cat.splits) {
      for (const p of pool) {
        if (s[p.ids].length === 0) continue; // platform not present in this row
        anyCell = true;
        const color = getPlatformColor(true, s[p.mine], s[p.comp], s[p.min], s[p.minMixed]);
        if (color === 'red') anyRed = true;
        else if (color === 'amber') anyAmber = true;
        else if (color === 'blue') anyBlue = true;
        if (color !== 'green') allGreen = false;
      }
    }
  }
  if (!anyCell) return null;
  if (anyRed) return 'red';
  if (anyAmber) return 'amber';
  if (anyBlue) return 'blue';
  if (allGreen) return 'green';
  return null;
}

// Platforms (from the given pool) that have listings in this split.
const presentPlatforms = (s: MergedSplit, pool: PlatformMeta[] = PLATFORMS) =>
  pool.filter((p) => s[p.ids].length > 0);

// The live bot's own duplicate-listing guard (livefootball.js) picks exactly ONE listing per exact
// (category, section, split) to actually compete on price — the lowest ticketId — and pins every
// other copy in that same bucket to a fixed decoy price it will never undercut from. A merged split
// row here is already bucketed by that identical (category[, section], split) key, so every id here
// beyond the first (same sort) IS one of those decoys: it carries no real floor, should never count
// toward "missing"/"mixed" minimum coverage, and should never be targeted by a minimum write — there's
// nothing to protect on a listing that's parked at an unsellable price on purpose.
function activeListingIds(ids: string[]): string[] {
  if (ids.length <= 1) return ids;
  return [[...ids].sort((a, b) => a.localeCompare(b))[0]];
}

// The ids a dashboard write may touch: the active (non-decoy) ids minus any whose minimum is LOCKED.
function editableIds(split: MergedSplit, meta: PlatformMeta, lockedKeys: Set<string>): string[] {
  return activeListingIds(split[meta.ids]).filter((id) => !lockedKeys.has(`${meta.key}:${id}`));
}

// True when every listing id of this platform in the split is locked (and there is at least one).
const fullyLocked = (split: MergedSplit, meta: PlatformMeta) =>
  split[meta.ids].length > 0 && split[meta.locked] >= split[meta.ids].length;

// isSplit: the given platforms don't all share the same minimum — either because they disagree with
// each other, or because one of them is internally mixed (its own listings don't agree). Either way
// there's no single number to show, so the input renders the "Split" placeholder instead of "—"
// (which would otherwise be indistinguishable from "no minimum was ever set").
function splitState(
  s: MergedSplit,
  platforms: PlatformMeta[] = presentPlatforms(s),
): { isSplit: boolean; shared: number | null } {
  if (platforms.length === 0) return { isSplit: false, shared: null };
  const mins = platforms.map((p) => s[p.min]);
  const anyMixed = platforms.some((p) => s[p.minMixed]);
  const allEqual = mins.every((m) => m === mins[0]);
  const isSplit = anyMixed || !allEqual;
  return { isSplit, shared: isSplit ? null : mins[0] };
}

// Returns a new split with the minimum applied to the given platform keys, recomputing the
// shared `minimum` so per-platform coloring updates immediately. Setting a platform's minimum here
// always covers EVERY listing ID in scope with the same value (the real write does too — see
// setMinimum/setMinimumsBulk), so it's safe to clear that platform's "mixed" flag optimistically.
function applyMinToSplit(split: MergedSplit, targets: PlatformKey[], price: number | null): MergedSplit {
  const next = { ...split };
  for (const key of targets) {
    const p = PLATFORMS.find((pl) => pl.key === key)!;
    // Locked ids keep their own floor: a fully locked platform is untouched by the write, and a
    // partly locked one now holds two values, which is exactly what "mixed" means.
    if (fullyLocked(next, p)) continue;
    if (next[p.locked] > 0) {
      next[p.min] = null;
      next[p.minMixed] = true;
      continue;
    }
    next[p.min] = price;
    next[p.minMixed] = false;
  }
  next.minimum = splitState(next).shared;
  return next;
}

interface MergedCategory {
  categoryName: string;
  splits: MergedSplit[];
}

interface MergedEvent {
  displayName: string;
  // Identity of this fixture (name + date, see listingsService.buildEventKeys). Two same-named
  // games on different dates are different rows, so this — not the name — is what the UI selects,
  // expands and keys React on.
  eventKey: string;
  // Plain normalised name, no date. This is what goes to the server on listing_minimums.
  normalisedEventName: string;
  eventDate: Date | null;
  categories: MergedCategory[];
  // Section-specific listings (Hello only) — rendered in a separate sub-table, excluded from title color.
  sections: MergedCategory[];
}

// Keep only splits that have ≥1 selected platform with listings; drop empty categories;
// return null if the event has nothing left under the current filter.
function filterEventByPlatforms(event: MergedEvent, selected: Set<PlatformKey>): MergedEvent | null {
  const pool = PLATFORMS.filter((p) => selected.has(p.key));
  const keepNonEmpty = (cats: MergedCategory[]) =>
    cats
      .map((cat) => ({ ...cat, splits: cat.splits.filter((s) => presentPlatforms(s, pool).length > 0) }))
      .filter((cat) => cat.splits.length > 0);
  const categories = keepNonEmpty(event.categories);
  const sections = keepNonEmpty(event.sections);
  if (categories.length === 0 && sections.length === 0) return null;
  return { ...event, categories, sections };
}

// Bulk-minimum scope: a whole match, one category in a match, or a category across all of a
// team's home games.
type BulkScope =
  | { kind: 'match'; event: MergedEvent }
  | { kind: 'category'; event: MergedEvent; cat: MergedCategory }
  // Section-specific (Hello-only): one section, or every section in the match.
  | { kind: 'section'; event: MergedEvent; cat: MergedCategory }
  | { kind: 'sectionAll'; event: MergedEvent }
  // onlyEvents (by normalisedEventName) restricts the team apply to a chosen subset of games.
  // splitType set ⇒ apply to only that split across the team's games (e.g. "Shortside Lower" +
  // "Up To 2 Together"); omitted ⇒ the whole category, as before.
  | { kind: 'teamCategory'; event: MergedEvent; cat: MergedCategory; onlyEvents?: Set<string>; splitType?: string };

// "Undercut" bulk: set each split's minimum to its lowest competitor price minus `x`.
// Scope decides which splits are affected (one split, a whole category, or the whole match).
type UndercutScope =
  | { kind: 'match'; event: MergedEvent }
  // All block-section rows of the match, WITHOUT the category-generic rows.
  | { kind: 'sectionAll'; event: MergedEvent }
  | { kind: 'category'; event: MergedEvent; cat: MergedCategory }
  | { kind: 'split'; event: MergedEvent; cat: MergedCategory; split: MergedSplit }
  // Across a team's home games: undercut one category (or a single split of it) on every selected game.
  | { kind: 'teamCategory'; event: MergedEvent; cat: MergedCategory; splitType?: string; onlyEvents?: Set<string> };

// Home team = the part of the (normalised) name before " vs " — home games only.
const homeTeamOf = (normalisedEventName: string) => normalisedEventName.split(/\s+vs\s+/)[0].trim();

// One platform's category-array contribution to the merge.
type CatSource = {
  rows: CategoryRow[] | undefined;
  platform: PlatformKey;
  mineField: 'liveMine' | 'ftnMine' | 'helloMine' | 'stubhubMine' | 'vividMine';
  compField: 'liveComp' | 'ftnComp' | 'helloComp' | 'stubhubComp' | 'vividComp';
  idsField: 'liveListingIds' | 'ftnListingIds' | 'helloListingIds' | 'stubhubListingIds' | 'vividListingIds';
};

// Merge one or more platforms' category-arrays into MergedCategory[] (grouped by normalised category
// name → split), then overlay current minimums. Reused for both the cross-platform category grid and
// the Hello-only section grid.
function buildMergedCategories(sources: CatSource[], minMap: Map<string, number>, lockedKeys: Set<string>): MergedCategory[] {
  const catMap = new Map<string, { categoryName: string; splitMap: Map<string, MergedSplit> }>();

  for (const { rows, platform, mineField, compField, idsField } of sources) {
    const lockedField = PLATFORMS.find((p) => p.key === platform)!.locked;
    for (const row of rows ?? []) {
      const catKey = row.normalisedCategoryName.toLowerCase();
      if (!catMap.has(catKey)) {
        catMap.set(catKey, { categoryName: row.normalisedCategoryName, splitMap: new Map() });
      }
      const { splitMap } = catMap.get(catKey)!;

      const ensureCell = (splitType: string): MergedSplit => {
        const splitKey = splitType.toLowerCase();
        if (!splitMap.has(splitKey)) {
          splitMap.set(splitKey, {
            splitType,
            liveMine: null, liveComp: null,
            ftnMine: null,  ftnComp: null,
            helloMine: null, helloComp: null,
            helloMineUsd: null, helloCompUsd: null,
            stubhubMine: null, stubhubComp: null,
            vividMine: null, vividComp: null,
            liveListingIds: [], ftnListingIds: [], helloListingIds: [], stubhubListingIds: [], vividListingIds: [],
            minimum: null,
            liveMinimum: null, ftnMinimum: null, helloMinimum: null, stubhubMinimum: null, vividMinimum: null,
            liveMinMixed: false, ftnMinMixed: false, helloMinMixed: false, stubhubMinMixed: false, vividMinMixed: false,
            liveLocked: 0, ftnLocked: 0, helloLocked: 0, stubhubLocked: 0, vividLocked: 0,
          });
        }
        return splitMap.get(splitKey)!;
      };

      for (const s of row.splitBreakdown ?? []) {
        const cell = ensureCell(s.splitType);
        if (s.myMinPrice != null) cell[mineField] = s.myMinPrice;
        if (s.competitorMinPrice != null) cell[compField] = s.competitorMinPrice;
        // Hello stores prices in USD too — keep the original for hover tooltips
        if (platform === 'hello') {
          if (s.myMinPriceUsd != null) cell.helloMineUsd = s.myMinPriceUsd;
          if (s.competitorMinPriceUsd != null) cell.helloCompUsd = s.competitorMinPriceUsd;
        }
      }

      // Collect this platform's listing IDs into the matching split row
      for (const l of row.myListings ?? []) {
        if (!l.splitType) continue;
        const cell = ensureCell(l.splitType);
        cell[idsField].push(l.listingId);
        if (lockedKeys.has(`${platform}:${l.listingId}`)) cell[lockedField] += 1;
      }

      // FTN: fill category-level competitor price into each split row (no per-split comp data)
      if (compField === 'ftnComp' && row.competitorMinPrice != null) {
        for (const cell of splitMap.values()) {
          if (cell.ftnComp == null) cell.ftnComp = row.competitorMinPrice;
        }
      }
    }
  }

  // Overlay current minimums — per-platform and shared.
  // For each platform: if every one of its listing IDs in this split has a minimumPrice AND they all
  // agree, show that number. Otherwise, as long as AT LEAST ONE id has a stored minimum, flag
  // `xMinMixed` instead of silently leaving the cell null — that covers both "all set but disagree"
  // (a legacy per-listing floor next to one just set) AND "some ids have no minimum at all" (a ticket
  // that turned over / relisted with a new listingId since the last bulk edit — very common right
  // after a "take down all" bulk action, since the live bot rescrapes and can hand back different
  // listingIds within minutes). Null is indistinguishable from "never set", which is what made a real,
  // bot-enforced floor look like it had vanished on refresh. Only when NONE of the ids have anything
  // stored is it left as plain null/red — genuinely never protected.
  const overlayPlatform = (
    rawIds: string[],
    platform: string,
  ): { mins: number[]; uniform: number | null; mixed: boolean } => {
    // Only the one active (non-decoy) listing counts for minimum coverage — see activeListingIds.
    const ids = activeListingIds(rawIds);
    if (ids.length === 0) return { mins: [], uniform: null, mixed: false };
    const mins = ids.map((id) => minMap.get(`${platform}:${id}`)).filter((v): v is number => v != null);
    const noneSet = mins.length === 0;
    const allSet = mins.length === ids.length;
    const allUniform = allSet && mins.every((v) => v === mins[0]);
    return { mins, uniform: allUniform ? mins[0] : null, mixed: !noneSet && !allUniform };
  };

  for (const { splitMap } of catMap.values()) {
    for (const cell of splitMap.values()) {
      const live = overlayPlatform(cell.liveListingIds, 'live');
      cell.liveMinimum = live.uniform;
      cell.liveMinMixed = live.mixed;
      const ftn = overlayPlatform(cell.ftnListingIds, 'ftn');
      cell.ftnMinimum = ftn.uniform;
      cell.ftnMinMixed = ftn.mixed;
      const hello = overlayPlatform(cell.helloListingIds, 'hello');
      cell.helloMinimum = hello.uniform;
      cell.helloMinMixed = hello.mixed;
      const stubhub = overlayPlatform(cell.stubhubListingIds, 'stubhub');
      cell.stubhubMinimum = stubhub.uniform;
      cell.stubhubMinMixed = stubhub.mixed;
      const vivid = overlayPlatform(cell.vividListingIds, 'vivid');
      cell.vividMinimum = vivid.uniform;
      cell.vividMinMixed = vivid.mixed;
      // Shared display value: only when all present platforms agree AND none of them is mixed
      const allMins = [...live.mins, ...ftn.mins, ...hello.mins, ...stubhub.mins, ...vivid.mins];
      const totalIds = cell.liveListingIds.length + cell.ftnListingIds.length + cell.helloListingIds.length + cell.stubhubListingIds.length + cell.vividListingIds.length;
      if (
        !live.mixed && !ftn.mixed && !hello.mixed && !stubhub.mixed && !vivid.mixed &&
        allMins.length === totalIds && totalIds > 0 && allMins.every((v) => v === allMins[0])
      ) {
        cell.minimum = allMins[0];
      }
    }
  }

  return Array.from(catMap.values()).map(({ categoryName, splitMap }) => ({
    categoryName,
    splits: Array.from(splitMap.values()).sort((a, b) => a.splitType.localeCompare(b.splitType)),
  }));
}

function mergeEvents(data: ListingsData, minMap: Map<string, number>, lockedKeys: Set<string>): MergedEvent[] {
  const allKeys = new Set([...data.live.keys(), ...data.ftn.keys(), ...data.hello.keys(), ...data.stubhub.keys(), ...data.vivid.keys()]);
  const merged: MergedEvent[] = [];

  for (const key of allKeys) {
    const live = data.live.get(key);
    const ftn = data.ftn.get(key);
    const hello = data.hello.get(key);
    const stubhub = data.stubhub.get(key);
    const vivid = data.vivid.get(key);
    const displayName = standardizeEventName(live?.eventName ?? ftn?.eventName ?? hello?.eventName ?? stubhub?.eventName ?? vivid?.eventName ?? key);

    const categories = buildMergedCategories([
      { rows: live?.categories,    platform: 'live',    mineField: 'liveMine',    compField: 'liveComp',    idsField: 'liveListingIds' },
      { rows: ftn?.categories,     platform: 'ftn',     mineField: 'ftnMine',     compField: 'ftnComp',     idsField: 'ftnListingIds' },
      { rows: hello?.categories,   platform: 'hello',   mineField: 'helloMine',   compField: 'helloComp',   idsField: 'helloListingIds' },
      { rows: stubhub?.categories, platform: 'stubhub', mineField: 'stubhubMine', compField: 'stubhubComp', idsField: 'stubhubListingIds' },
      { rows: vivid?.categories,   platform: 'vivid',   mineField: 'vividMine',   compField: 'vividComp',   idsField: 'vividListingIds' },
    ], minMap, lockedKeys);

    // Section-specific (block-level) listings, rendered in their own sub-table. Live football carries
    // block data too (grouped by sectionName by the live bot), alongside FTN, Hello and StubHub.
    const sections = buildMergedCategories([
      { rows: live?.sections, platform: 'live', mineField: 'liveMine', compField: 'liveComp', idsField: 'liveListingIds' },
      // FTN listings pinned to a block (ftnSyncListings.mjs writes them as "<category> <block>").
      { rows: ftn?.sections, platform: 'ftn', mineField: 'ftnMine', compField: 'ftnComp', idsField: 'ftnListingIds' },
      { rows: hello?.sections, platform: 'hello', mineField: 'helloMine', compField: 'helloComp', idsField: 'helloListingIds' },
      { rows: stubhub?.sections, platform: 'stubhub', mineField: 'stubhubMine', compField: 'stubhubComp', idsField: 'stubhubListingIds' },
      { rows: vivid?.sections,   platform: 'vivid',   mineField: 'vividMine',   compField: 'vividComp',   idsField: 'vividListingIds' },
    ], minMap, lockedKeys);

    // The EARLIEST date any platform carries. A fixture without a fixed kick-off gets a placeholder
    // per platform — Vivid stamps 23:59 UTC on a day inside the matchweek window, which reads as the
    // next day in local time — and the earliest is the one least likely to have been padded.
    const stamps = [live, ftn, hello, stubhub, vivid]
      .map((p) => (p?.eventDate ? new Date(p.eventDate).getTime() : NaN))
      .filter((t) => Number.isFinite(t));
    const eventDate = stamps.length ? new Date(Math.min(...stamps)) : null;

    // Past games are noise here: show only today/future (yesterday kept for day-after handling).
    // Undated events stay — no date is no proof the game is over.
    const startOfYesterday = new Date();
    startOfYesterday.setHours(0, 0, 0, 0);
    startOfYesterday.setDate(startOfYesterday.getDate() - 1);
    if (eventDate && eventDate < startOfYesterday) continue;

    const normalisedEventName =
      live?.normalisedEventName ?? ftn?.normalisedEventName ?? hello?.normalisedEventName ?? stubhub?.normalisedEventName ?? vivid?.normalisedEventName ?? key;
    merged.push({ displayName, eventKey: key, normalisedEventName, eventDate, categories, sections });
  }

  return merged.sort((a, b) => {
    if (a.eventDate && b.eventDate) return a.eventDate.getTime() - b.eventDate.getTime();
    if (a.eventDate) return -1;
    if (b.eventDate) return 1;
    return a.displayName.localeCompare(b.displayName);
  });
}

/**
 * One event card. The match-name header sticks to the top while scrolling, and the
 * column-header row (Category / platform labels) sticks right beneath it. Both
 * scroll away once the next match's card reaches the top, because each is
 * position:sticky within its own card (the card is the containing block).
 *
 * `overflow-visible` on the card overrides Ionic's default `overflow:hidden`, which
 * would otherwise break sticky. The table has no overflow wrapper for the same reason.
 */
const EventCard: React.FC<{
  event: MergedEvent;
  isExpanded: boolean;
  onToggle: () => void;
  selected: Set<PlatformKey>;
  onSaveMinimum: (
    event: MergedEvent,
    cat: MergedCategory,
    split: MergedSplit,
    platforms: PlatformKey[],
    raw: string | number,
  ) => Promise<boolean>;
  onBulkSave: (scope: BulkScope, raw: string | number) => Promise<boolean>;
  onUndercut: (scope: UndercutScope, x: string | number) => Promise<boolean>;
  // Lock / unlock the minimum on the given platforms' listings in this split.
  onToggleLock: (event: MergedEvent, cat: MergedCategory, split: MergedSplit, platforms: PlatformKey[], locked: boolean) => Promise<boolean>;
  teamCategoryGames: (event: MergedEvent, cat: MergedCategory, splitType?: string) => { event: MergedEvent; listings: number }[];
}> = ({ event, isExpanded, onToggle, selected, onSaveMinimum, onBulkSave, onUndercut, onToggleLock, teamCategoryGames }) => {
  const headerRef = useRef<HTMLIonCardHeaderElement>(null);
  const [editor, setEditor] = useState<{ catName: string; splitType: string; ev: any } | null>(null);
  // Bulk-minimum editor (match-level or category-level), with its own typed value.
  const [bulkEditor, setBulkEditor] = useState<
    | { kind: 'match'; ev: any }
    | { kind: 'category'; catName: string; ev: any }
    | { kind: 'section'; catName: string; ev: any }
    | { kind: 'sectionAll'; ev: any }
    | null
  >(null);
  const [bulkValue, setBulkValue] = useState('');
  // Team-category game-selection modal: pick which of the team's home games to apply to.
  const [teamModal, setTeamModal] = useState<{ catName: string; splitType?: string } | null>(null);
  const [teamModalValue, setTeamModalValue] = useState('');

  // "Take block sections down": flip every Hello block-section listing in this match to inactive
  // (off sale) via the bulk status endpoint. Category-generic listings are untouched — only the
  // section rows' hello ids are sent.
  const [sectionsBusy, setSectionsBusy] = useState(false);
  const [sectionsToast, setSectionsToast] = useState<string | null>(null);
  const takeSectionsDown = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const ids = Array.from(
      new Set(event.sections.flatMap((c) => c.splits.flatMap((s) => s.helloListingIds))),
    );
    if (!ids.length) {
      setSectionsToast('No Hello block-section listings in this match');
      return;
    }
    if (!window.confirm(`Take DOWN ${ids.length} Hello block-section listing(s) for ${event.displayName}?\n\nCategory listings are NOT touched. This can take ~15s.`)) return;
    setSectionsBusy(true);
    try {
      const r = await setHelloListingsStatus(ids, 'inactive');
      setSectionsToast(`Took down ${r.changed} of ${r.requested} block-section listings`);
    } catch (err: any) {
      setSectionsToast(err?.response?.data?.message || err?.message || 'Failed to take sections down');
    } finally {
      setSectionsBusy(false);
    }
  };
  const [teamSelected, setTeamSelected] = useState<Set<string>>(new Set());
  const [headerH, setHeaderH] = useState(0);
  // Undercut amount (£/$) shared within this match card — set min to lowest comp minus this.
  const [undercutX, setUndercutX] = useState('10');

  // Small "undercut by x" control: a number box + a button that sets the scope's minimums to
  // (lowest competitor price − x). Reused at match / category / split level.
  const undercutControl = (scope: UndercutScope, label: string) => (
    <span className="flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
      <input
        type="number"
        min="0"
        step="0.5"
        value={undercutX}
        onChange={(e) => setUndercutX(e.target.value)}
        title="Undercut amount below the lowest competitor price"
        className="w-11 text-xs text-right rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-1 py-0.5 focus:outline-none focus:border-primary-500"
      />
      <button
        title={`Set min to (lowest comp − ${undercutX || 0}) for this ${label}`}
        onClick={(e) => { e.stopPropagation(); onUndercut(scope, undercutX); }}
        className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
      >
        <IonIcon icon={trendingDownOutline} className="text-gray-500 dark:text-gray-400" />
      </button>
    </span>
  );

  // Measure the match-name header so the column header can stick directly below it
  useLayoutEffect(() => {
    if (!isExpanded) return;
    const el = headerRef.current;
    if (!el) return;
    const measure = () => setHeaderH(el.offsetHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [isExpanded]);

  const thBase =
    'px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-gray-50 dark:bg-gray-800 sticky z-10';
  const thStyle = { top: headerH };

  // Platforms currently shown (and edited), in registry order.
  const visible = PLATFORMS.filter((p) => selected.has(p.key));

  // World Cup events are kept in USD (no conversion), so show prices with a $ sign; others are GBP.
  const cur = /world\s*cup/i.test(event.normalisedEventName) ? '$' : '£';

  // Resolve the split currently being edited in the popover (re-derived each render so it
  // stays fresh after optimistic updates).
  const editorCat = editor ? event.categories.find((c) => c.categoryName === editor.catName) : undefined;
  const editorSplit = editorCat?.splits.find((s) => s.splitType === editor!.splitType);
  const editorRowPlatforms = editorSplit ? presentPlatforms(editorSplit, visible) : [];

  // Aggregate status color for the match title — scoped to the visible platforms.
  const titleColor = getEventTitleColor(event, visible);

  // Listing count over a set of splits for the visible (selected) platforms — for bulk labels.
  const countListings = (splits: MergedSplit[]) => {
    let n = 0;
    for (const s of splits) for (const p of visible) n += s[p.ids].length;
    return n;
  };
  const bulkCat =
    bulkEditor?.kind === 'category' ? event.categories.find((c) => c.categoryName === bulkEditor.catName)
    : bulkEditor?.kind === 'section' ? event.sections.find((c) => c.categoryName === bulkEditor.catName)
    : undefined;
  // Column pool for the Sections sub-table — Live, Hello + StubHub carry block-specific listings.
  const sectionPool = visible.filter((p) => p.key === 'live' || p.key === 'ftn' || p.key === 'hello' || p.key === 'stubhub');
  // Block-sections sub-table is collapsible (collapsed by default — it can be very long).
  // Collapsed by default (it can be very long) — unless the match has NO category rows at all, which
  // is the FTN case: every FTN listing is pinned to a block, so with the sub-table closed the match
  // looked empty. Open it up front then, so the blocks are the first thing shown.
  const [sectionsOpen, setSectionsOpen] = useState(() => event.categories.length === 0 && event.sections.length > 0);
  const teamLabel = event.displayName.split(/\s+vs\s+/i)[0].trim();

  // Team-category game-selection modal derivations.
  const modalCat = teamModal ? event.categories.find((c) => c.categoryName === teamModal.catName) : undefined;
  const teamGames = modalCat ? teamCategoryGames(event, modalCat, teamModal?.splitType) : [];
  const teamSelectedListings = teamGames
    .filter((g) => teamSelected.has(g.event.eventKey))
    .reduce((a, g) => a + g.listings, 0);

  // Renders a listings table for the given groups over the given platform column pool. `mode`
  // controls the first header label and the per-group bulk scope ('category' vs Hello-only 'section').
  const renderTable = (groups: MergedCategory[], pool: PlatformMeta[], mode: 'category' | 'section') => (
    // Phone: the wrapper scrolls sideways (sticky headers are sacrificed below sm — without a scroll
    // container the 10-column table overflows the page body and nothing is reachable). Desktop keeps
    // overflow visible so the sticky match/column headers still work (see EventCard doc comment).
    <div className="overflow-x-auto sm:overflow-visible">
    <table className="w-full min-w-[560px] sm:min-w-0">
      <thead>
        <tr className="border-b border-gray-200 dark:border-gray-700">
          <th className={`${thBase} text-left`} style={thStyle}>{mode === 'section' ? 'Section' : 'Category'}</th>
          {pool.map((p) => (
            <React.Fragment key={p.key}>
              <th className={`${thBase} text-right`} style={thStyle}>Mine · {p.label}</th>
              <th className={`${thBase} text-right`} style={thStyle}>Comp · {p.label}</th>
            </React.Fragment>
          ))}
          <th className={`${thBase} text-right`} style={thStyle}>Min</th>
        </tr>
      </thead>
      <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
        {groups.map((cat) => (
          <React.Fragment key={cat.categoryName}>
            {/* Group header row */}
            <tr className="bg-gray-100 dark:bg-gray-750">
              <td
                colSpan={1 + pool.length * 2 + 1}
                className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300"
              >
                <div className="flex items-center justify-between">
                  <span>{cat.categoryName}</span>
                  <span className="flex items-center gap-1">
                    {undercutControl({ kind: 'category', event, cat }, mode)}
                    <button
                      title={`Bulk-set minimum for this ${mode}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setBulkValue('');
                        setBulkEditor({ kind: mode, catName: cat.categoryName, ev: e.nativeEvent });
                      }}
                      className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                    >
                      <IonIcon icon={pricetagOutline} className="text-gray-500 dark:text-gray-400" />
                    </button>
                  </span>
                </div>
              </td>
            </tr>
            {/* One row per split */}
            {cat.splits.map((s) => {
              const colorByKey: Record<string, CellColor> = {};
              for (const p of pool) {
                colorByKey[p.key] = getPlatformColor(s[p.ids].length > 0, s[p.mine], s[p.comp], s[p.min], s[p.minMixed]);
              }
              const present = Object.values(colorByKey).filter((c): c is NonNullable<CellColor> => c != null);
              const rowColor: CellColor =
                present.length >= 2 && present.every((c) => c === present[0]) ? present[0] : null;
              const rc = rowColor ? COLOR_CLASS[rowColor] : '';
              const cellBg = (p: PlatformMeta) =>
                rowColor ? rc : (colorByKey[p.key] ? COLOR_CLASS[colorByKey[p.key]!] : '');

              const rowPlatforms = presentPlatforms(s, pool);
              const scoped = splitState(s, rowPlatforms);
              const lockedTotal = rowPlatforms.reduce((n, p) => n + s[p.locked], 0);
              const idsTotal = rowPlatforms.reduce((n, p) => n + s[p.ids].length, 0);
              const allLocked = idsTotal > 0 && lockedTotal >= idsTotal;
              const lockTitle = allLocked
                ? `Locked — all ${idsTotal} listing(s) here keep their current minimum; edits skip them and the bot leaves their price alone. Click to unlock.`
                : lockedTotal > 0
                  ? `${lockedTotal} of ${idsTotal} listing(s) here are locked (kept at their own minimum, bot hands off); edits only touch the rest. Click to unlock them.`
                  : 'Lock the minimum on every listing in this row (freeze price: edits skip them, bot hands off)';
              return (
                <tr
                  key={s.splitType}
                  className={`transition-colors ${rc || 'hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                >
                  <td className={`px-4 py-2 pl-8 text-sm text-gray-700 dark:text-gray-300 ${rc}`}>
                    {s.splitType}
                  </td>
                  {pool.map((p) => (
                    <React.Fragment key={p.key}>
                      <td className={`px-4 py-2 text-sm text-right font-medium text-gray-900 dark:text-gray-100 ${cellBg(p)}`}>
                        {p.key === 'hello' && cur !== '$' ? (
                          <span title={s.helloMineUsd != null ? `$${s.helloMineUsd.toFixed(2)} USD` : undefined}>{fmt(s[p.mine], cur)}</span>
                        ) : fmt(s[p.mine], cur)}
                      </td>
                      <td className={`px-4 py-2 text-sm text-right text-gray-600 dark:text-gray-300 ${cellBg(p)}`}>
                        {p.key === 'hello' && cur !== '$' ? (
                          <span title={s.helloCompUsd != null ? `$${s.helloCompUsd.toFixed(2)} USD` : undefined}>{fmt(s[p.comp], cur)}</span>
                        ) : fmt(s[p.comp], cur)}
                      </td>
                    </React.Fragment>
                  ))}
                  <td className={`px-4 py-2 text-sm text-right ${rc}`}>
                    {rowPlatforms.length === 0 ? (
                      <span className="text-gray-400 dark:text-gray-600">–</span>
                    ) : (
                      <div className="flex items-center justify-end gap-1">
                        {allLocked ? (
                          <span
                            title={lockTitle}
                            className="inline-flex items-center justify-end w-20 px-2 py-1 text-sm font-medium text-gray-500 dark:text-gray-400 border border-dashed border-gray-300 dark:border-gray-600 rounded"
                          >
                            {scoped.isSplit ? 'Split' : fmt(scoped.shared, cur)}
                          </span>
                        ) : (
                          <MinInput
                            value={scoped.shared}
                            placeholder={scoped.isSplit ? 'Split' : '—'}
                            title={
                              rowPlatforms.some((p) => s[p.minMixed])
                                ? 'Not every listing here has the same floor — either an older per-listing value sits next to one just set, or a listing turned over/relisted since the last bulk edit and has no floor yet. The bot still enforces whatever each listing already has — type a value and press Enter to make them all match.'
                                : undefined
                            }
                            onCommit={(val) => onSaveMinimum(event, cat, s, rowPlatforms.map((p) => p.key), val)}
                          />
                        )}
                        <button
                          title={lockTitle}
                          onClick={(e) => {
                            e.stopPropagation();
                            void onToggleLock(event, cat, s, rowPlatforms.map((p) => p.key), lockedTotal === 0);
                          }}
                          className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                        >
                          <IonIcon
                            icon={lockedTotal > 0 ? lockClosedOutline : lockOpenOutline}
                            className={allLocked ? 'text-amber-600 dark:text-amber-400' : lockedTotal > 0 ? 'text-amber-500' : 'text-gray-300 dark:text-gray-600'}
                          />
                        </button>
                        {rowPlatforms.length >= 2 && (
                          <button
                            title="Set minimum per platform"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditor({ catName: cat.categoryName, splitType: s.splitType, ev: e.nativeEvent });
                            }}
                            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            <IonIcon
                              icon={optionsOutline}
                              className={scoped.isSplit ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 dark:text-gray-500'}
                            />
                          </button>
                        )}
                        {mode === 'category' && (
                          <button
                            title={`Set this split's minimum across all ${teamLabel} home games`}
                            onClick={(e) => {
                              e.stopPropagation();
                              const games = teamCategoryGames(event, cat, s.splitType);
                              setTeamModalValue('');
                              setTeamSelected(new Set(games.map((g) => g.event.eventKey)));
                              setTeamModal({ catName: cat.categoryName, splitType: s.splitType });
                            }}
                            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            <IonIcon icon={albumsOutline} className="text-gray-400 dark:text-gray-500" />
                          </button>
                        )}
                        {undercutControl({ kind: 'split', event, cat, split: s }, 'listing')}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </React.Fragment>
        ))}
      </tbody>
    </table>
    </div>
  );

  return (
    <IonCard className="shadow-lg" style={{ overflow: 'visible' }}>
      <IonCardHeader
        ref={headerRef}
        className="cursor-pointer select-none sticky top-0 z-20 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700"
        onClick={onToggle}
      >
        <IonCardTitle className="flex items-center justify-between text-base font-semibold text-gray-900 dark:text-gray-100">
          <span className={titleColor ? TITLE_COLOR_CLASS[titleColor] : 'text-gray-900 dark:text-gray-100'}>
            {event.displayName}
            {event.eventDate && (
              <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
                — {format(event.eventDate, 'dd MMM yyyy')}
              </span>
            )}
          </span>
          <span className="flex items-center gap-1 flex-shrink-0 ml-2">
            {undercutControl({ kind: 'match', event }, 'match')}
            <button
              title="Set minimum for the whole match"
              onClick={(e) => {
                e.stopPropagation();
                setBulkValue('');
                setBulkEditor({ kind: 'match', ev: e.nativeEvent });
              }}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <IonIcon icon={pricetagOutline} className="text-gray-400 dark:text-gray-500" />
            </button>
            <IonIcon
              icon={isExpanded ? chevronDownOutline : chevronForwardOutline}
              className="text-gray-400 dark:text-gray-500"
            />
          </span>
        </IonCardTitle>
      </IonCardHeader>
      {isExpanded && (
        <IonCardContent className="p-0">
          {renderTable(event.categories, visible, 'category')}

          {/* Block-specific listings (Hello + StubHub) — separate collapsible sub-table at the
              bottom, excluded from the match-title color. */}
          {event.sections.length > 0 && sectionPool.length > 0 && (
            <div className="mt-4 border-t-2 border-gray-200 dark:border-gray-700">
              <div
                onClick={() => setSectionsOpen((o) => !o)}
                className="flex items-center justify-between px-4 py-2 bg-gray-50 dark:bg-gray-800 cursor-pointer select-none hover:bg-gray-100 dark:hover:bg-gray-700"
                title={sectionsOpen ? 'Hide block sections' : 'Show block sections'}
              >
                <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  <IonIcon icon={sectionsOpen ? chevronDownOutline : chevronForwardOutline} />
                  Block sections ({event.sections.length})
                </span>
                {sectionsOpen && (
                  <span className="flex items-center gap-1">
                    {undercutControl({ kind: 'sectionAll', event }, 'block-sections table (every block, categories untouched)')}
                    <button
                      title="Bulk-set minimum for all block sections in this match"
                      onClick={(e) => {
                        e.stopPropagation();
                        setBulkValue('');
                        setBulkEditor({ kind: 'sectionAll', ev: e.nativeEvent });
                      }}
                      className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                    >
                      <IonIcon icon={pricetagOutline} className="text-gray-500 dark:text-gray-400" />
                    </button>
                    <button
                      title="Take ALL block-section listings in this match OFF SALE on Hello (category listings untouched)"
                      onClick={takeSectionsDown}
                      disabled={sectionsBusy}
                      className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30 disabled:opacity-50"
                    >
                      {sectionsBusy
                        ? <IonSpinner name="crescent" style={{ width: 14, height: 14 }} />
                        : <IonIcon icon={cloudOfflineOutline} className="text-red-500 dark:text-red-400" />}
                    </button>
                  </span>
                )}
              </div>
              {sectionsOpen && renderTable(event.sections, sectionPool, 'section')}
            </div>
          )}
          <IonToast
            isOpen={!!sectionsToast}
            message={sectionsToast || ''}
            duration={4500}
            onDidDismiss={() => setSectionsToast(null)}
          />
        </IonCardContent>
      )}

      <IonPopover
        isOpen={!!editor}
        event={editor?.ev}
        onDidDismiss={() => setEditor(null)}
        showBackdrop={false}
      >
        {editorCat && editorSplit && (
          <div className="p-3 min-w-[230px]">
            <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
              Minimum per platform
            </div>
            {editorRowPlatforms.map((p) => (
              <div key={p.key} className="flex items-center justify-between gap-3 py-1">
                <span className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                  <span className={`w-2.5 h-2.5 rounded-full ${p.dot}`} />
                  {p.label}
                </span>
                <span className="flex items-center gap-1">
                  {fullyLocked(editorSplit, p) ? (
                    <span className="inline-flex items-center justify-end w-20 px-2 py-1 text-sm font-medium text-gray-500 dark:text-gray-400 border border-dashed border-gray-300 dark:border-gray-600 rounded">
                      {fmt(editorSplit[p.min], cur)}
                    </span>
                  ) : (
                    <MinInput
                      value={editorSplit[p.min]}
                      onCommit={(val) => onSaveMinimum(event, editorCat, editorSplit, [p.key], val)}
                    />
                  )}
                  <button
                    title={
                      editorSplit[p.locked] > 0
                        ? `${editorSplit[p.locked]} of ${editorSplit[p.ids].length} ${p.label} listing(s) locked — click to unlock`
                        : `Lock ${p.label}'s minimum here (freeze price: edits skip it, bot hands off)`
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      void onToggleLock(event, editorCat, editorSplit, [p.key], editorSplit[p.locked] === 0);
                    }}
                    className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <IonIcon
                      icon={editorSplit[p.locked] > 0 ? lockClosedOutline : lockOpenOutline}
                      className={editorSplit[p.locked] > 0 ? 'text-amber-500' : 'text-gray-300 dark:text-gray-600'}
                    />
                  </button>
                </span>
              </div>
            ))}
            <div className="flex items-center justify-between gap-3 mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
              <span className="text-sm text-gray-500 dark:text-gray-400">Set all</span>
              <MinInput
                value={splitState(editorSplit, editorRowPlatforms).shared}
                placeholder={splitState(editorSplit, editorRowPlatforms).isSplit ? 'Split' : '—'}
                onCommit={(val) => onSaveMinimum(event, editorCat, editorSplit, editorRowPlatforms.map((p) => p.key), val)}
              />
            </div>
          </div>
        )}
      </IonPopover>

      {/* Bulk minimum (match / category / team-category) */}
      <IonPopover
        isOpen={!!bulkEditor}
        event={bulkEditor?.ev}
        onDidDismiss={() => setBulkEditor(null)}
        showBackdrop={false}
      >
        <div className="p-3 min-w-[250px] space-y-2">
          <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
            {bulkEditor?.kind === 'match'
              ? 'Minimum · whole match'
              : bulkEditor?.kind === 'sectionAll'
                ? 'Minimum · all sections'
                : `Minimum · ${bulkCat?.categoryName ?? ''}`}
          </div>
          <input
            type="number"
            inputMode="decimal"
            step="0.01"
            min="0"
            autoFocus
            value={bulkValue}
            placeholder="£ min (blank = clear)"
            onChange={(e) => setBulkValue(e.target.value)}
            className="w-full text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-2 py-1.5 focus:outline-none focus:border-primary-500"
          />
          {bulkEditor?.kind === 'match' && (
            <button
              onClick={async () => {
                const ok = await onBulkSave({ kind: 'match', event }, bulkValue);
                if (ok) setBulkEditor(null);
              }}
              className="w-full text-sm rounded px-3 py-1.5 bg-primary-600 text-white hover:bg-primary-700"
            >
              Apply to whole match ({countListings(event.categories.flatMap((c) => c.splits))})
            </button>
          )}
          {bulkEditor?.kind === 'category' && bulkCat && (
            <>
              <button
                onClick={async () => {
                  const ok = await onBulkSave({ kind: 'category', event, cat: bulkCat }, bulkValue);
                  if (ok) setBulkEditor(null);
                }}
                className="w-full text-sm rounded px-3 py-1.5 bg-primary-600 text-white hover:bg-primary-700"
              >
                This match ({countListings(bulkCat.splits)})
              </button>
              <button
                onClick={() => {
                  const games = teamCategoryGames(event, bulkCat);
                  setTeamModalValue(bulkValue);
                  setTeamSelected(new Set(games.map((g) => g.event.eventKey)));
                  setBulkEditor(null);
                  setTeamModal({ catName: bulkCat.categoryName });
                }}
                className="w-full text-sm rounded px-3 py-1.5 border border-primary-400 text-primary-700 dark:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/30"
              >
                Choose {teamLabel} home games ({teamCategoryGames(event, bulkCat).length})…
              </button>
            </>
          )}
          {bulkEditor?.kind === 'section' && bulkCat && (
            <button
              onClick={async () => {
                const ok = await onBulkSave({ kind: 'section', event, cat: bulkCat }, bulkValue);
                if (ok) setBulkEditor(null);
              }}
              className="w-full text-sm rounded px-3 py-1.5 bg-primary-600 text-white hover:bg-primary-700"
            >
              Apply to section ({countListings(bulkCat.splits)})
            </button>
          )}
          {bulkEditor?.kind === 'sectionAll' && (
            <button
              onClick={async () => {
                const ok = await onBulkSave({ kind: 'sectionAll', event }, bulkValue);
                if (ok) setBulkEditor(null);
              }}
              className="w-full text-sm rounded px-3 py-1.5 bg-primary-600 text-white hover:bg-primary-700"
            >
              Apply to all sections ({countListings(event.sections.flatMap((c) => c.splits))})
            </button>
          )}
        </div>
      </IonPopover>

      {/* Team-category game picker */}
      <IonModal
        isOpen={!!teamModal}
        onDidDismiss={() => setTeamModal(null)}
        style={{ '--width': '440px', '--max-width': '94vw', '--height': 'auto', '--max-height': '82vh' } as React.CSSProperties}
      >
        {modalCat && (
          <div className="flex flex-col max-h-[82vh] bg-white dark:bg-gray-900">
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div className="font-semibold text-gray-900 dark:text-gray-100">
                {modalCat.categoryName}{teamModal?.splitType ? ` · ${teamModal.splitType}` : ''} · {teamLabel} home games
              </div>
              <button onClick={() => setTeamModal(null)} className="text-sm text-gray-500 dark:text-gray-400 hover:underline">
                Close
              </button>
            </div>
            <div className="px-4 py-3 space-y-3 overflow-y-auto">
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                  Minimum (£) — blank clears
                </label>
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0"
                  value={teamModalValue}
                  onChange={(e) => setTeamModalValue(e.target.value)}
                  placeholder="£ min"
                  className="w-full text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-2 py-1.5 focus:outline-none focus:border-primary-500"
                />
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-400">{teamSelected.size} of {teamGames.length} selected</span>
                <button
                  onClick={() =>
                    setTeamSelected(
                      teamSelected.size === teamGames.length
                        ? new Set()
                        : new Set(teamGames.map((g) => g.event.eventKey)),
                    )
                  }
                  className="text-primary-600 dark:text-primary-400 hover:underline"
                >
                  {teamSelected.size === teamGames.length ? 'Deselect all' : 'Select all'}
                </button>
              </div>
              <ul className="divide-y divide-gray-200 dark:divide-gray-700 border-y border-gray-200 dark:border-gray-700">
                {teamGames.map((g) => {
                  const id = g.event.eventKey;
                  return (
                    <li key={id}>
                      <label className="flex items-center gap-3 py-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={teamSelected.has(id)}
                          onChange={() =>
                            setTeamSelected((prev) => {
                              const n = new Set(prev);
                              n.has(id) ? n.delete(id) : n.add(id);
                              return n;
                            })
                          }
                        />
                        <span className="flex-1 text-sm text-gray-900 dark:text-gray-100">
                          {g.event.displayName}
                          {g.event.eventDate && (
                            <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                              {format(g.event.eventDate, 'dd MMM yyyy')}
                            </span>
                          )}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">{g.listings}</span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </div>
            <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 space-y-2">
              <button
                disabled={teamSelected.size === 0}
                onClick={async () => {
                  const ok = await onBulkSave(
                    { kind: 'teamCategory', event, cat: modalCat, onlyEvents: teamSelected, splitType: teamModal?.splitType },
                    teamModalValue,
                  );
                  if (ok) setTeamModal(null);
                }}
                className="w-full text-sm rounded px-3 py-2 bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
              >
                Apply min to {teamSelected.size} game(s) ({teamSelectedListings} listings)
              </button>
              {/* Or: set each split's min to (lowest comp − x) across the selected games */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">or undercut comp by</span>
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={undercutX}
                  onChange={(e) => setUndercutX(e.target.value)}
                  className="w-14 text-sm text-right rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-2 py-1 focus:outline-none focus:border-primary-500"
                />
                <button
                  disabled={teamSelected.size === 0}
                  onClick={async () => {
                    const ok = await onUndercut(
                      { kind: 'teamCategory', event, cat: modalCat, splitType: teamModal?.splitType, onlyEvents: teamSelected },
                      undercutX,
                    );
                    if (ok) setTeamModal(null);
                  }}
                  className="flex-1 flex items-center justify-center gap-1 text-sm rounded px-3 py-2 border border-primary-400 text-primary-700 dark:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/30 disabled:opacity-50"
                >
                  <IonIcon icon={trendingDownOutline} />
                  Undercut on {teamSelected.size} game(s)
                </button>
              </div>
            </div>
          </div>
        )}
      </IonModal>
    </IonCard>
  );
};

const ListingsContent: React.FC = () => {
  const [events, setEvents] = useState<MergedEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedEvents, setExpandedEvents] = useState<Set<string>>(new Set());
  const [toast, setToast] = useState<string | null>(null);
  // Team filter — when set, only that team's home games ("<team> vs …") are shown.
  const [sortTeam, setSortTeam] = useState('');
  // Free-text game search — when set, only games whose name matches are shown (live).
  const [gameSearch, setGameSearch] = useState('');
  // "Take down minimums by x" amount for the visible-games bulk action (local state only — never
  // read in a render-body derivation, so typing here / in search stays cheap).
  // Keys (`${platform}:${listingId}`) of every LOCKED minimum — those ids are left out of every write
  // here (the server refuses them too) and rendered read-only with a lock.
  const [lockedKeys, setLockedKeys] = useState<Set<string>>(() => new Set());
  const [takeDownX, setTakeDownX] = useState('10');
  const [takingDown, setTakingDown] = useState(false);
  // When a take-down would hit many games, hold the prepared items here and confirm before writing.
  const [pendingTakeDown, setPendingTakeDown] = useState<
    {
      items: ListingMinimumItem[];
      games: number;
      skipped: number;
      edits: Map<MergedSplit, { key: PlatformKey; newMin: number }[]>;
    } | null
  >(null);
  // Show/hide World Cup games (UI-only filter; never touches their data or minimums). Persisted.
  const [showWorldCup, setShowWorldCup] = useState<boolean>(() => {
    try { return JSON.parse(localStorage.getItem('listingsShowWorldCup') ?? 'true'); } catch { return true; }
  });
  useEffect(() => {
    localStorage.setItem('listingsShowWorldCup', JSON.stringify(showWorldCup));
  }, [showWorldCup]);

  // Platform filter — which platforms' columns are shown (and edited). Persisted.
  const [selected, setSelected] = useState<Set<PlatformKey>>(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('listingsPlatformFilter') || '[]') as PlatformKey[];
      const valid = saved.filter((k) => PLATFORMS.some((p) => p.key === k));
      if (valid.length) return new Set(valid);
    } catch { /* ignore */ }
    return new Set(PLATFORMS.map((p) => p.key));
  });

  useEffect(() => {
    localStorage.setItem('listingsPlatformFilter', JSON.stringify([...selected]));
  }, [selected]);

  const togglePlatform = useCallback((key: PlatformKey) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        if (next.size === 1) return prev; // keep at least one selected
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const selectAllPlatforms = useCallback(() => setSelected(new Set(PLATFORMS.map((p) => p.key))), []);

  const toggleEvent = useCallback((name: string) => {
    setExpandedEvents((prev) => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  }, []);

  const load = async () => {
    try {
      setError(null);
      setLoading(true);
      const [data, minimums, locked] = await Promise.all([
        getAllListings(),
        getMinimums(),
        // An older server without the lock route must not break the screen — nothing is locked then.
        getLockedMinimums().catch(() => new Set<string>()),
      ]);
      // `minimums` is now a compact { `${platform}:${listingId}`: price } map from the server. Tolerate
      // the OLD array shape too, so client/server can deploy in either order without breaking the screen.
      const minMap = new Map<string, number>(
        Array.isArray(minimums)
          ? (minimums as any[]).map((m) => [`${m.platform}:${m.listingId}`, m.minimumPrice])
          : Object.entries(minimums as Record<string, number>),
      );
      setLockedKeys(locked);
      setEvents(mergeEvents(data, minMap, locked));
      setExpandedEvents(new Set()); // collapse all on fresh load
    } catch (err: any) {
      setError(err.message || 'Failed to load listings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleRefresh = async (e: CustomEvent<RefresherEventDetail>) => {
    await load();
    e.detail.complete();
  };

  // Save a minimum to the given platforms' listing IDs in this split (one keystroke can target
  // one platform, several, or all — driven by the platform filter / popover).
  const handleSaveMinimum = async (
    event: MergedEvent,
    cat: MergedCategory,
    split: MergedSplit,
    platforms: PlatformKey[],
    raw: string | number,
  ): Promise<boolean> => {
    const str = String(raw).trim();
    const price = str === '' ? null : Number(str);
    if (price != null && (isNaN(price) || price < 0)) {
      setToast('Invalid minimum price');
      return false;
    }

    const ctx = {
      normalisedEventName: event.normalisedEventName,
      categoryName: cat.categoryName,
      splitType: split.splitType,
    };
    const listings: TargetListing[] = platforms.flatMap((key) => {
      const meta = PLATFORMS.find((p) => p.key === key)!;
      return editableIds(split, meta, lockedKeys).map((id) => ({ platform: key, listingId: id, ...ctx }));
    });

    if (listings.length === 0) {
      const lockedHere = platforms.reduce((n, key) => n + split[PLATFORMS.find((p) => p.key === key)!.locked], 0);
      setToast(lockedHere > 0 ? 'Every listing in this row is locked — unlock first to change its minimum' : 'No listings to apply a minimum to in this row');
      return false;
    }

    // Optimistic update — `cat` lives in exactly one of categories/sections, so map both safely.
    const applyToGroups = (groups: MergedCategory[]) =>
      groups.map((c) =>
        c !== cat
          ? c
          : { ...c, splits: c.splits.map((s) => (s !== split ? s : applyMinToSplit(s, platforms, price))) },
      );
    setEvents((prev) =>
      prev.map((ev) =>
        ev !== event
          ? ev
          : { ...ev, categories: applyToGroups(ev.categories), sections: applyToGroups(ev.sections) },
      ),
    );

    try {
      const res = await setMinimum(price, listings);
      if (res.lockedSkipped > 0) setToast(`Saved — ${res.lockedSkipped} locked listing(s) kept their own minimum`);
      return true;
    } catch (err: any) {
      setToast(err.message || 'Failed to save minimum');
      await load(); // revert to server truth
      return false;
    }
  };

  // Lock / unlock the minimum on the given platforms' listings in this split. Locked = frozen: the
  // listing keeps whatever minimum it has, every write here skips it, and the bots leave its price
  // alone. Every id in the split is locked (decoys included) so nothing on the listing can move.
  const handleToggleLock = async (
    event: MergedEvent,
    cat: MergedCategory,
    split: MergedSplit,
    platforms: PlatformKey[],
    locked: boolean,
  ): Promise<boolean> => {
    const ctx = {
      normalisedEventName: event.normalisedEventName,
      categoryName: cat.categoryName,
      splitType: split.splitType,
    };
    const listings: TargetListing[] = platforms.flatMap((key) => {
      const meta = PLATFORMS.find((p) => p.key === key)!;
      return split[meta.ids].map((id) => ({ platform: key, listingId: id, ...ctx }));
    });
    if (listings.length === 0) return false;

    const keys = new Set(lockedKeys);
    for (const l of listings) {
      const k = `${l.platform}:${l.listingId}`;
      if (locked) keys.add(k);
      else keys.delete(k);
    }
    setLockedKeys(keys);
    const patch = (s: MergedSplit): MergedSplit => {
      const next = { ...s };
      for (const key of platforms) {
        const meta = PLATFORMS.find((p) => p.key === key)!;
        next[meta.locked] = locked ? next[meta.ids].length : 0;
      }
      return next;
    };
    const applyToGroups = (groups: MergedCategory[]) =>
      groups.map((c) => (c !== cat ? c : { ...c, splits: c.splits.map((s) => (s !== split ? s : patch(s))) }));
    setEvents((prev) =>
      prev.map((ev) => (ev !== event ? ev : { ...ev, categories: applyToGroups(ev.categories), sections: applyToGroups(ev.sections) })),
    );

    try {
      await setMinimumsLocked(listings, locked);
      setToast(`${locked ? 'Locked' : 'Unlocked'} ${listings.length} listing(s)`);
      return true;
    } catch (err: any) {
      setToast(err.message || `Failed to ${locked ? 'lock' : 'unlock'}`);
      await load();
      return false;
    }
  };

  // The team's home games that have this category with selected-platform listings (+ each
  // game's listing count) — drives the game-selection modal.
  const teamCategoryGames = useCallback(
    (event: MergedEvent, cat: MergedCategory, splitType?: string): { event: MergedEvent; listings: number }[] => {
      const team = homeTeamOf(event.normalisedEventName);
      const keys = PLATFORMS.filter((p) => selected.has(p.key));
      const out: { event: MergedEvent; listings: number }[] = [];
      for (const ev of events) {
        if (homeTeamOf(ev.normalisedEventName) !== team) continue;
        const c = ev.categories.find((cc) => cc.categoryName.toLowerCase() === cat.categoryName.toLowerCase());
        if (!c) continue;
        let n = 0;
        for (const s of c.splits) {
          if (splitType && s.splitType.toLowerCase() !== splitType.toLowerCase()) continue;
          for (const p of keys) n += s[p.ids].length;
        }
        if (n > 0) out.push({ event: ev, listings: n });
      }
      return out;
    },
    [events, selected],
  );

  // Bulk-set a minimum across a match, a category, or a category across a team's home games.
  const handleBulkSave = async (scope: BulkScope, raw: string | number): Promise<boolean> => {
    const str = String(raw).trim();
    const price = str === '' ? null : Number(str);
    if (price != null && (isNaN(price) || price < 0)) {
      setToast('Invalid minimum price');
      return false;
    }
    const keys = PLATFORMS.filter((p) => selected.has(p.key)).map((p) => p.key);

    // Resolve the (event, cat, split) tuples in scope.
    const targets: { ev: MergedEvent; cat: MergedCategory; split: MergedSplit }[] = [];
    if (scope.kind === 'match') {
      for (const c of scope.event.categories) for (const s of c.splits) targets.push({ ev: scope.event, cat: c, split: s });
    } else if (scope.kind === 'category') {
      for (const s of scope.cat.splits) targets.push({ ev: scope.event, cat: scope.cat, split: s });
    } else if (scope.kind === 'section') {
      for (const s of scope.cat.splits) targets.push({ ev: scope.event, cat: scope.cat, split: s });
    } else if (scope.kind === 'sectionAll') {
      for (const c of scope.event.sections) for (const s of c.splits) targets.push({ ev: scope.event, cat: c, split: s });
    } else {
      const team = homeTeamOf(scope.event.normalisedEventName);
      const only = scope.onlyEvents;
      for (const ev of events) {
        if (homeTeamOf(ev.normalisedEventName) !== team) continue;
        if (only && !only.has(ev.eventKey)) continue;
        const c = ev.categories.find((cc) => cc.categoryName.toLowerCase() === scope.cat.categoryName.toLowerCase());
        if (!c) continue;
        for (const s of c.splits) {
          if (scope.splitType && s.splitType.toLowerCase() !== scope.splitType.toLowerCase()) continue;
          targets.push({ ev, cat: c, split: s });
        }
      }
    }

    // Build the listing targets (selected platforms only) with per-listing context.
    const listings: TargetListing[] = [];
    for (const t of targets) {
      const ctx = {
        normalisedEventName: t.ev.normalisedEventName,
        categoryName: t.cat.categoryName,
        splitType: t.split.splitType,
      };
      for (const key of keys) {
        const meta = PLATFORMS.find((p) => p.key === key)!;
        for (const id of editableIds(t.split, meta, lockedKeys)) listings.push({ platform: key, listingId: id, ...ctx });
      }
    }
    if (listings.length === 0) {
      setToast('No listings in scope for the selected platform(s)');
      return false;
    }

    // Optimistic update — replace the targeted splits by reference identity (in categories or sections).
    const splitSet = new Set(targets.map((t) => t.split));
    const mapGroups = (groups: MergedCategory[], onChange: () => void) =>
      groups.map((c) => {
        let catChanged = false;
        const splits = c.splits.map((s) => {
          if (!splitSet.has(s)) return s;
          catChanged = true;
          onChange();
          return applyMinToSplit(s, keys, price);
        });
        return catChanged ? { ...c, splits } : c;
      });
    setEvents((prev) =>
      prev.map((ev) => {
        let changed = false;
        const mark = () => { changed = true; };
        const categories = mapGroups(ev.categories, mark);
        const sections = mapGroups(ev.sections, mark);
        return changed ? { ...ev, categories, sections } : ev;
      }),
    );

    try {
      const res = await setMinimum(price, listings);
      const verb = price == null ? 'Cleared minimum' : `Set £${price}`;
      const lockNote = res.lockedSkipped > 0 ? ` — ${res.lockedSkipped} locked kept` : '';
      if (scope.kind === 'teamCategory') {
        const team = scope.event.displayName.split(/\s+vs\s+/i)[0].trim();
        const games = new Set(targets.map((t) => t.ev)).size;
        const label = scope.splitType ? `${scope.cat.categoryName} · ${scope.splitType}` : scope.cat.categoryName;
        setToast(`${verb} on ${label} for ${games} ${team} home game(s) (${listings.length} listing(s))${lockNote}`);
      } else {
        setToast(`${verb} on ${listings.length} listing(s)${lockNote}`);
      }
      return true;
    } catch (err: any) {
      setToast(err.message || 'Failed to save minimums');
      await load();
      return false;
    }
  };

  // Undercut bulk: set each split's minimum to (its lowest competitor price − x) for the visible
  // platforms. Each split gets its own value (its own lowest comp), so listings are grouped by
  // the resulting price and persisted in one call per distinct price.
  const handleUndercut = async (scope: UndercutScope, raw: string | number): Promise<boolean> => {
    const x = Number(String(raw).trim());
    if (!Number.isFinite(x) || x < 0) { setToast('Invalid undercut amount'); return false; }
    const pool = PLATFORMS.filter((p) => selected.has(p.key));
    const keys = pool.map((p) => p.key);

    const splits: { ev: MergedEvent; cat: MergedCategory; split: MergedSplit }[] = [];
    if (scope.kind === 'split') {
      splits.push({ ev: scope.event, cat: scope.cat, split: scope.split });
    } else if (scope.kind === 'category') {
      for (const s of scope.cat.splits) splits.push({ ev: scope.event, cat: scope.cat, split: s });
    } else if (scope.kind === 'teamCategory') {
      const team = homeTeamOf(scope.event.normalisedEventName);
      const only = scope.onlyEvents;
      for (const ev of events) {
        if (homeTeamOf(ev.normalisedEventName) !== team) continue;
        if (only && !only.has(ev.eventKey)) continue;
        const c = ev.categories.find((cc) => cc.categoryName.toLowerCase() === scope.cat.categoryName.toLowerCase());
        if (!c) continue;
        for (const s of c.splits) {
          if (scope.splitType && s.splitType.toLowerCase() !== scope.splitType.toLowerCase()) continue;
          splits.push({ ev, cat: c, split: s });
        }
      }
    } else if (scope.kind === 'sectionAll') {
      // Block sections only — the category-generic rows are deliberately left alone.
      for (const c of scope.event.sections)
        for (const s of c.splits) splits.push({ ev: scope.event, cat: c, split: s });
    } else {
      for (const c of [...scope.event.categories, ...scope.event.sections])
        for (const s of c.splits) splits.push({ ev: scope.event, cat: c, split: s });
    }

    const splitNewMin = new Map<MergedSplit, number>();
    const byPrice = new Map<number, TargetListing[]>();
    let skippedNoComp = 0;
    for (const t of splits) {
      const comps = pool.map((p) => t.split[p.comp]).filter((v): v is number => v != null);
      // No competitor → NO floor. This used to fall back to "my current price minus x", which on a
      // $3,000 placeholder wrote a £2,210 minimum; the bot then treated that as intent and RAISED
      // sixteen live £90-£170 Crystal Palace listings to $2,986. A floor derived from my own price
      // is never what "undercut the competition" means, and with nothing to undercut the right
      // answer is to leave the split alone and say so.
      if (!comps.length) { skippedNoComp++; continue; }
      const base = Math.min(...comps);                       // undercut the cheapest competitor
      const newMin = Math.max(0, Number((base - x).toFixed(2)));
      splitNewMin.set(t.split, newMin);
      const ctx = {
        normalisedEventName: t.ev.normalisedEventName,
        categoryName: t.cat.categoryName,
        splitType: t.split.splitType,
      };
      for (const p of pool) {
        for (const id of editableIds(t.split, p, lockedKeys)) {
          if (!byPrice.has(newMin)) byPrice.set(newMin, []);
          byPrice.get(newMin)!.push({ platform: p.key, listingId: id, ...ctx });
        }
      }
    }

    const total = [...byPrice.values()].reduce((a, l) => a + l.length, 0);
    if (total === 0) { setToast(skippedNoComp ? `Nothing to undercut — ${skippedNoComp} split${skippedNoComp === 1 ? '' : 's'} have no competitor price, so no minimum was set` : 'Nothing in scope to take down'); return false; }

    setEvents((prev) =>
      prev.map((ev) => {
        let changed = false;
        const mapG = (groups: MergedCategory[]) =>
          groups.map((c) => {
            let cc = false;
            const sp = c.splits.map((s) => {
              if (!splitNewMin.has(s)) return s;
              cc = true; changed = true;
              return applyMinToSplit(s, keys, splitNewMin.get(s)!);
            });
            return cc ? { ...c, splits: sp } : c;
          });
        const categories = mapG(ev.categories);
        const sections = mapG(ev.sections);
        return changed ? { ...ev, categories, sections } : ev;
      }),
    );

    try {
      await Promise.all([...byPrice.entries()].map(([price, listings]) => setMinimum(price, listings)));
      const games = new Set(splits.filter((t) => splitNewMin.has(t.split)).map((t) => t.ev)).size;
      setToast(`Took down ${total} listing(s) by ${x}${scope.kind === 'teamCategory' ? ` across ${games} game(s)` : ''}`);
      return true;
    } catch (err: any) {
      setToast(err.message || 'Failed to save minimums');
      await load();
      return false;
    }
  };

  // ── Take down minimums across all visible games (one click) ──────────────────
  // Build the per-listing items, honoring the selected-platform filter. Rule per split × platform:
  //   competitor present → base = competitor → ALWAYS sets the floor to (cheapest comp − x)
  //   no competitor      → base = my price   → sets the floor to (my current price − x)
  //   neither            → skip (nothing to reference)
  // The target is absolute (comp − x), never relative to the existing minimum — so re-clicking is
  // idempotent (stays at comp − x) instead of walking the floor down by x each time.
  // newMin = max(0, base − x); one item per listingId.
  const buildTakeDownItems = (targetEvents: MergedEvent[], x: number) => {
    const pool = PLATFORMS.filter((p) => selected.has(p.key));
    const items: ListingMinimumItem[] = [];
    // In-place edits so we can update the table without a full reload: split → {platform → newMin}.
    const edits = new Map<MergedSplit, { key: PlatformKey; newMin: number }[]>();
    let games = 0;
    let skipped = 0;
    for (const ev of targetEvents) {
      let touched = false;
      for (const cat of [...ev.categories, ...ev.sections]) {
        for (const s of cat.splits) {
          const ctx = {
            normalisedEventName: ev.normalisedEventName,
            categoryName: cat.categoryName,
            splitType: s.splitType,
          };
          for (const p of pool) {
            const ids = editableIds(s, p, lockedKeys);
            if (ids.length === 0) continue;
            const comp = s[p.comp];
            const curMin = s[p.min];
            const mine = s[p.mine];
            let base: number | null;
            if (comp != null) base = comp;        // competition → floor = cheapest competitor − x
            else if (mine != null) base = mine;   // no competition → floor = my current price − x
            else base = null;                     // no competitor and no price → nothing to reference
            if (base == null) { skipped += ids.length; continue; }
            const newMin = Math.max(0, Number((base - x).toFixed(2)));
            if (curMin != null && newMin === curMin) continue; // already at target → no redundant write
            for (const id of ids) items.push({ platform: p.key, listingId: id, minimumPrice: newMin, ...ctx });
            if (!edits.has(s)) edits.set(s, []);
            edits.get(s)!.push({ key: p.key, newMin });
            touched = true;
          }
        }
      }
      if (touched) games++;
    }
    return { items, games, skipped, edits };
  };

  // Apply the prepared per-split minimum changes to the table in place (no refetch). Mirrors the
  // optimistic update in handleSaveMinimum, but each platform in a split can get its own price.
  const applyTakeDownEdits = (edits: Map<MergedSplit, { key: PlatformKey; newMin: number }[]>) => {
    if (edits.size === 0) return;
    const patchSplit = (s: MergedSplit): MergedSplit => {
      const changes = edits.get(s);
      if (!changes) return s;
      const next = { ...s };
      for (const { key, newMin } of changes) {
        const p = PLATFORMS.find((pl) => pl.key === key)!;
        next[p.min] = newMin;
        next[p.minMixed] = false; // this write covers every listing id in scope — uniform now
      }
      next.minimum = splitState(next).shared;
      return next;
    };
    const patchGroups = (groups: MergedCategory[]) =>
      groups.map((c) => ({ ...c, splits: c.splits.map(patchSplit) }));
    setEvents((prev) =>
      prev.map((ev) => ({ ...ev, categories: patchGroups(ev.categories), sections: patchGroups(ev.sections) })),
    );
  };

  // Persist the prepared items (sequential chunked bulk requests). Updates the table in place so
  // it's instant — only falls back to a full reload if a batch fails (to correct any drift).
  // Honest reporting: surfaces skipped listings and any failed batch.
  const commitTakeDown = async (
    items: ListingMinimumItem[],
    games: number,
    skipped: number,
    edits: Map<MergedSplit, { key: PlatformKey; newMin: number }[]>,
  ) => {
    applyTakeDownEdits(edits); // optimistic, instant — no full refetch on the happy path
    setTakingDown(true);
    try {
      const { updated, failedChunks, lockedSkipped } = await setMinimumsBulk(items);
      const extra =
        (skipped ? `, ${skipped} skipped` : '') +
        (lockedSkipped ? `, ${lockedSkipped} locked kept` : '') +
        (failedChunks ? `, ${failedChunks} batch(es) FAILED` : '');
      setToast(`Took down ${updated} listing(s) across ${games} game(s)${extra}`);
      if (failedChunks) await load(); // some didn't land → reload to show the true state
    } catch (err: any) {
      setToast(err.message || 'Failed to take down minimums');
      await load();
    } finally {
      setTakingDown(false);
    }
  };

  const CONFIRM_THRESHOLD_GAMES = 10;
  const handleTakeDown = async (targetEvents: MergedEvent[], raw: string | number) => {
    const x = Number(String(raw).trim());
    if (!Number.isFinite(x) || x <= 0) { setToast('Enter a positive amount to take down'); return; }
    const { items, games, skipped, edits } = buildTakeDownItems(targetEvents, x);
    if (items.length === 0) { setToast('Nothing to take down in the visible games'); return; }
    if (games > CONFIRM_THRESHOLD_GAMES) {
      setPendingTakeDown({ items, games, skipped, edits }); // confirm before a big write
      return;
    }
    await commitTakeDown(items, games, skipped, edits);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-16">
        <IonSpinner name="crescent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 py-8 text-center">
        <IonText color="danger">
          <p>{error}</p>
        </IonText>
      </div>
    );
  }

  // Home teams available for the "show first" dropdown — the part before "vs" in each event name.
  const homeTeams = Array.from(
    new Set(
      events
        .map((e) => e.displayName.match(/^(.+?)\s+vs\b/i)?.[1].trim())
        .filter((t): t is string => !!t),
    ),
  ).sort((a, b) => a.localeCompare(b));

  let visibleEvents = events
    .map((e) => filterEventByPlatforms(e, selected))
    .filter((e): e is MergedEvent => e !== null);

  // UI-only: hide World Cup games (name contains "World Cup", or the word "Match") when the toggle
  // is off. This only affects what's rendered — the underlying events (and their minimums) are
  // untouched, and no bulk action can reach them.
  if (!showWorldCup) {
    visibleEvents = visibleEvents.filter((e) => !/world\s*cup|\bmatch\b/i.test(e.normalisedEventName));
  }

  // Team filter — show ONLY the chosen team's home games ("<team> vs …"). Display-only; the other
  // events (and all minimums) are untouched, and bulk actions still operate on the full data set.
  if (sortTeam) {
    const prefix = `${sortTeam.toLowerCase()} vs`;
    visibleEvents = visibleEvents.filter((e) => e.displayName.toLowerCase().startsWith(prefix));
  }

  // Search filter — show only games whose name matches what's typed (live, as you type).
  const gameQuery = gameSearch.trim().toLowerCase();
  if (gameQuery) {
    visibleEvents = visibleEvents.filter((e) => e.displayName.toLowerCase().includes(gameQuery));
  }

  return (
    <>
      <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
        <IonRefresherContent />
      </IonRefresher>

      <div className="p-4 space-y-4">
        {/* Platform filter */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 mr-1">
            Platforms
          </span>
          <button
            onClick={selectAllPlatforms}
            className={`px-3 py-1 rounded-full text-sm border transition-colors ${
              selected.size === PLATFORMS.length
                ? 'bg-primary-50 dark:bg-primary-900/30 border-primary-400 text-primary-700 dark:text-primary-300'
                : 'bg-transparent border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400'
            }`}
          >
            All
          </button>
          {PLATFORMS.map((p) => {
            const on = selected.has(p.key);
            return (
              <button
                key={p.key}
                onClick={() => togglePlatform(p.key)}
                className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-sm border transition-colors ${
                  on
                    ? 'bg-primary-50 dark:bg-primary-900/30 border-primary-400 text-primary-700 dark:text-primary-300'
                    : 'bg-transparent border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400'
                }`}
              >
                <span className={`w-2 h-2 rounded-full ${p.dot} ${on ? '' : 'opacity-40'}`} />
                {p.label}
              </button>
            );
          })}
        </div>

        {/* Filter by team / search a game */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 mr-1">
            Team
          </span>
          <select
            value={sortTeam}
            onChange={(e) => setSortTeam(e.target.value)}
            className="px-3 py-1 rounded-full text-sm border bg-transparent border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 focus:outline-none focus:border-primary-400"
          >
            <option value="">All teams</option>
            {homeTeams.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>

          <input
            type="text"
            value={gameSearch}
            onChange={(e) => setGameSearch(e.target.value)}
            placeholder="Search a game…"
            className="px-3 py-1 rounded-full text-sm border bg-transparent border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-primary-400 w-44"
          />

          {(sortTeam || gameSearch) && (
            <button
              onClick={() => { setSortTeam(''); setGameSearch(''); }}
              className="px-3 py-1 rounded-full text-sm border border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              Reset
            </button>
          )}

          {/* Show / hide World Cup games (display only) */}
          <label className="ml-auto flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showWorldCup}
              onChange={(e) => setShowWorldCup(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
            />
            Show World Cup
          </label>
        </div>

        {/* Take down minimums across every game currently shown (respects platform + team + search).
            Drives each listing's floor below the competitor (or shaves my price where there's no
            competition), in one click, via a single chunked bulk request. */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 mr-1">
            Take down
          </span>
          <input
            type="number"
            min="0"
            step="0.5"
            value={takeDownX}
            onChange={(e) => setTakeDownX(e.target.value)}
            title="Amount to lower every visible listing's minimum by"
            className="w-16 text-sm text-right rounded border border-gray-300 dark:border-gray-600 bg-transparent text-gray-700 dark:text-gray-200 px-2 py-1 focus:outline-none focus:border-primary-400"
          />
          <button
            type="button"
            disabled={takingDown}
            onClick={() => handleTakeDown(visibleEvents, takeDownX)}
            title="Lower the minimum on every listing in the games currently shown"
            className="flex items-center gap-1 px-3 py-1 rounded-full text-sm border border-amber-400 text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {takingDown ? <IonSpinner name="dots" className="w-4 h-4" /> : <IonIcon icon={trendingDownOutline} />}
            Take down {visibleEvents.length} visible game(s)
          </button>
        </div>

        {events.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 dark:text-gray-400">No listings found.</p>
          </div>
        ) : visibleEvents.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 dark:text-gray-400">No listings for the selected platform(s).</p>
          </div>
        ) : (
          visibleEvents.map((event) => (
            <EventCard
              // Key by eventKey (unique per fixture — name AND date) — NOT displayName, which can
              // collide (e.g. "Liverpool FC vs Sunderland" and StubHub's "…vs Sunderland AFC" both
              // display as "Liverpool vs Sunderland" but are separate events). A duplicate key made
              // React leave ghost rows that ignored the platform/search filters and wouldn't expand.
              key={event.eventKey}
              event={event}
              isExpanded={expandedEvents.has(event.eventKey)}
              onToggle={() => toggleEvent(event.eventKey)}
              selected={selected}
              onSaveMinimum={handleSaveMinimum}
              onBulkSave={handleBulkSave}
              onUndercut={handleUndercut}
              onToggleLock={handleToggleLock}
              teamCategoryGames={teamCategoryGames}
            />
          ))
        )}
      </div>

      <IonToast
        isOpen={toast != null}
        message={toast ?? ''}
        duration={2500}
        onDidDismiss={() => setToast(null)}
      />

      {/* Guard a large take-down behind a confirm so an accidental all-games click can't slip through. */}
      <IonAlert
        isOpen={pendingTakeDown != null}
        header="Take down minimums?"
        message={
          pendingTakeDown
            ? `This lowers the minimum on ${pendingTakeDown.items.length} listing(s) across ${pendingTakeDown.games} game(s)${pendingTakeDown.skipped ? ` (${pendingTakeDown.skipped} skipped)` : ''}.`
            : ''
        }
        buttons={[
          { text: 'Cancel', role: 'cancel', handler: () => setPendingTakeDown(null) },
          {
            text: 'Take down',
            handler: () => {
              const p = pendingTakeDown;
              setPendingTakeDown(null);
              if (p) void commitTakeDown(p.items, p.games, p.skipped, p.edits);
            },
          },
        ]}
        onDidDismiss={() => setPendingTakeDown(null)}
      />
    </>
  );
};

export default ListingsContent;
